"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { type RemoteServerStatus, type WorkerRun, type WorkersData, type WorkerStatus, type WorkerThroughput } from "@/lib/workersData";

type MaintenanceStatus = WorkersData["maintenance"];
type MaintenanceUiMode = "running" | "preparing" | "maintenance" | "launching";
type MonitorAgent = WorkersData["monitorAgents"][number];

function formatDuration(ms: number) {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function formatRatio(value: number) {
  if (!Number.isFinite(value) || value <= 0) {
    return "0.00x";
  }
  if (Math.abs(value - 1) < 0.05) {
    return `${value.toFixed(3)}x`;
  }
  return `${value.toFixed(2)}x`;
}

function formatAge(seconds: number | null) {
  if (seconds === null) {
    return "never";
  }
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m ago`;
  }
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`;
}

function formatDate(ts: number) {
  if (!ts) {
    return "-";
  }
  return new Date(ts * 1000).toLocaleString();
}

function ThroughputBlock({ label, value }: { label: string; value: WorkerThroughput }) {
  return (
    <div className="workerMetricBlock">
      <strong>{formatRatio(value.ratio)}</strong>
      <span>{label}</span>
      <small>
        {formatDuration(value.processedMs)} video / {formatDuration(value.elapsedMs)} elapsed
      </small>
    </div>
  );
}

function CountBlock({ label, value, detail }: { label: string; value: number; detail?: string }) {
  return (
    <div className="workerMetricBlock">
      <strong>{value}</strong>
      <span>{label}</span>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function WorkerCard({ worker }: { worker: WorkerStatus }) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function setEnabled(enabled: boolean) {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`/api/workers/${worker.id}/enabled`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ enabled })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "worker toggle failed");
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "worker toggle failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`workerCard${worker.disabled ? " disabled" : ""}`}>
      <header className="workerCardHeader">
        <div>
          <h2>{worker.name}</h2>
          <p>{worker.host || "no host"} · {worker.role}</p>
        </div>
        <div className="workerCardHeaderActions">
          {worker.roleCode === 1 ? (
            <button
              type="button"
              className={`workerEnabledToggle ${worker.disabled ? "off" : "on"}`}
              disabled={busy}
              aria-pressed={!worker.disabled}
              title={worker.disabled
                ? "Disabled: no new jobs are claimed. In-flight jobs finish normally."
                : "Enabled: claims jobs normally. Disable to stop new claims."}
              onClick={() => void setEnabled(worker.disabled)}
            >
              {busy ? "…" : worker.disabled ? "enable" : "disable"}
            </button>
          ) : null}
          <span className={`workerStatusPill ${worker.online ? "online" : "offline"}`}>
            {worker.online ? "online" : "offline"}
          </span>
        </div>
      </header>

      <div className="workerCardMeta">
        <span>heartbeat {formatAge(worker.heartbeatAgeSec)}</span>
        {worker.disabled ? <span className="workerDisabledNote">disabled — no new jobs</span> : null}
      </div>
      {error ? <p className="workerNote">{error}</p> : null}

      {worker.roleCode === 1 ? (
        <div className="workerMetricsGrid">
          <ThroughputBlock label="last 24h vs playback" value={worker.last24h} />
          <ThroughputBlock label="all time vs playback" value={worker.allTime} />
        </div>
      ) : null}

      {worker.roleCode !== 1 ? (
        <div className="workerMetricsGrid">
          <CountBlock label="uploaded buffer" value={worker.uploadedBuffer} />
          <CountBlock label="uploading" value={worker.uploadingBuffer} />
        </div>
      ) : null}

      <div className="workerJobList">
        {worker.currentJobs.length ? (
          worker.currentJobs.map((job) => (
            <div className="workerJobRow" key={`${job.id}-${job.videoId}`}>
              <span className="workerJobTitle">{job.title}</span>
              <span className="workerJobState">
                {job.status}
                {job.progressLabel && job.progressLabel !== job.status ? ` · ${job.progressLabel}` : ""}
              </span>
            </div>
          ))
        ) : (
          <div className="workerJobRow idle">idle</div>
        )}
      </div>
    </article>
  );
}

function MonitorAgentCard({ agent }: { agent: MonitorAgent }) {
  return (
    <article className="workerCard">
      <header className="workerCardHeader">
        <div>
          <h2>{agent.type === "autodl" ? "AutoDL Agent" : agent.type === "ktcloud" ? "KT Cloud Agent" : agent.key}</h2>
          <p>{agent.key}</p>
        </div>
        <span className={`workerStatusPill ${agent.online ? "online" : "offline"}`}>
          {agent.online ? "online" : "offline"}
        </span>
      </header>
      <div className="workerCardMeta">
        <span>heartbeat {formatAge(agent.heartbeatAgeSec)}</span>
        <span>source {formatAge(agent.sourceObservedAgeSec)}</span>
      </div>
    </article>
  );
}

function RemoteServerCard({ server }: { server: RemoteServerStatus }) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const connection = server.sshHost && server.sshUser
    ? `${server.sshUser}@${server.sshHost}:${server.sshPort}`
    : server.serverKey;
  const statusDetail = [server.provider, server.serverRole, server.providerStatus, server.startMode].filter(Boolean).join(" · ");
  const statusLabel = server.runtimeStatusCode === 3
    ? "online"
    : server.runtimeStatusCode === 2
      ? "no-GPU"
      : server.runtimeStatus;
  const statusClassName = server.runtimeStatusCode === 3
    ? "online"
    : server.runtimeStatusCode === 2
      ? "remote noGpu"
      : `remote status${server.runtimeStatusCode}`;
  const controllable = /^ktcloud:gpuk[01]$/.test(server.serverKey);
  const desiredRunning = server.comfyDesiredState !== "stopped";

  async function control(action: "start" | "stop") {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`/api/servers/${server.id}/comfy`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || `Comfy ${action} failed`);
      window.setTimeout(() => router.refresh(), action === "start" ? 3000 : 1500);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : `Comfy ${action} failed`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="workerCard remoteServerCard">
      <header className="workerCardHeader">
        <div>
          <h2>{server.displayName}</h2>
          <p>{statusDetail || connection}</p>
        </div>
        <span className={`workerStatusPill ${statusClassName}`}>{statusLabel}</span>
      </header>
      <div className="workerCardMeta">
        <span>seen {formatAge(server.lastSeenAgeSec)}</span>
        <span>changed {formatAge(server.lastChangedAgeSec)}</span>
        <span>{connection}</span>
        {server.sshWorkdir ? <span>{server.sshWorkdir}</span> : null}
      </div>
      <div className="workerMetricsGrid">
        <div className="workerMetricBlock">
          <strong>{server.gpuIdleNum}/{server.gpuTotalNum}</strong>
          <span>idle gpu</span>
        </div>
        <div className="workerMetricBlock">
          <strong>{server.gpuUsedNum}</strong>
          <span>used gpu</span>
        </div>
        <div className="workerMetricBlock">
          <strong>{server.runningTasks}</strong>
          <span>running</span>
        </div>
        <div className="workerMetricBlock">
          <strong>{server.pendingTasks}</strong>
          <span>pending</span>
        </div>
      </div>
      {server.note ? <p className="workerNote">{server.note}</p> : null}
      {controllable ? (
        <div className="remoteServerActions">
          <span>Comfy auto-recovery {desiredRunning ? "on" : "off"}</span>
          <button
            type="button"
            disabled={busy}
            className={desiredRunning ? "stop" : "start"}
            onClick={() => void control(desiredRunning ? "stop" : "start")}
          >
            {busy ? "Applying..." : desiredRunning ? "Stop Comfy" : "Start Comfy"}
          </button>
          {error ? <small>{error}</small> : null}
        </div>
      ) : null}
    </article>
  );
}

function RecentRunRow({ run }: { run: WorkerRun }) {
  return (
    <div className="workerRunRow">
      <span>{run.workerName}</span>
      <span>{run.title}</span>
      <span>{run.status}</span>
      <span>{formatRatio(run.ratio)}</span>
      <span>{formatDuration(run.processedMs)}</span>
      <span>{formatDuration(run.elapsedMs)}</span>
      <span>{run.shotCount}</span>
      <span>{formatDate(run.finishedTs || run.startedTs)}</span>
    </div>
  );
}

function maintenanceModeLabel(status: MaintenanceStatus, launching: boolean): MaintenanceUiMode {
  if (launching) {
    return "launching";
  }
  if (status.modeLabel === "maintenance_entering") {
    return "preparing";
  }
  if (status.modeLabel === "maintenance_active") {
    return "maintenance";
  }
  return "running";
}

function maintenanceTitle(mode: MaintenanceUiMode) {
  if (mode === "preparing") {
    return "Prepare maintenance";
  }
  if (mode === "maintenance") {
    return "Maintenance";
  }
  if (mode === "launching") {
    return "Launching";
  }
  return "Running";
}

function maintenanceDescription(status: MaintenanceStatus, mode: MaintenanceUiMode) {
  if (mode === "preparing") {
    return `${status.blockerCount} active task${status.blockerCount === 1 ? "" : "s"} draining. New tasks are blocked.`;
  }
  if (mode === "maintenance") {
    return "Workers are holding at safe points. Server restart, code update, and DB migration are safe.";
  }
  if (mode === "launching") {
    return "Workers can claim jobs again. Refreshing worker state.";
  }
  return "Workers can claim new jobs normally.";
}

function MaintenancePanel({ initial }: { initial: MaintenanceStatus }) {
  const router = useRouter();
  const [status, setStatus] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [launching, setLaunching] = useState(false);

  async function refresh() {
    const response = await fetch("/api/ops/maintenance", { cache: "no-store" });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || "maintenance refresh failed");
    }
    setStatus(payload.maintenance);
    return payload.maintenance as MaintenanceStatus;
  }

  async function update(action: "enter" | "cancel" | "exit") {
    setBusy(true);
    try {
      const response = await fetch("/api/ops/maintenance", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action, requestedBy: "web" })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || "maintenance update failed");
      }
      setStatus(payload.maintenance);
      if (action === "exit") {
        setLaunching(true);
        window.setTimeout(() => {
          setLaunching(false);
          router.refresh();
        }, 5000);
        return;
      }
      router.refresh();
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (status.modeLabel === "normal" && !launching) {
      return;
    }
    const timer = window.setInterval(() => {
      refresh().then(() => router.refresh()).catch(() => {});
    }, 5000);
    return () => window.clearInterval(timer);
  }, [launching, router, status.modeLabel]);

  const mode = maintenanceModeLabel(status, launching);
  const action =
    mode === "preparing"
      ? { label: "Cancel Preparation", onClick: () => update("cancel"), variant: "neutral" }
      : mode === "maintenance"
        ? { label: "Exit Maintenance", onClick: () => update("exit"), variant: "resume" }
        : mode === "launching"
          ? { label: "Launching...", onClick: () => {}, variant: "resume" }
          : { label: "Prepare Maintenance", onClick: () => update("enter"), variant: "danger" };

  return (
    <section className={`maintenancePanel ${mode}`}>
      <div>
        <h2>{maintenanceTitle(mode)}</h2>
        <p>{maintenanceDescription(status, mode)}</p>
        <div className="maintenanceStats">
          <span>encoding {status.activeEncoding}</span>
          <span>inference {status.activeMotion}</span>
          <span>safe holds {status.safeHeldEncoding + status.safeHeldMotion}</span>
        </div>
      </div>
      <button
        className={`maintenanceButton ${action.variant}`}
        type="button"
        disabled={busy || mode === "launching"}
        onClick={action.onClick}
      >
        {busy ? "Working..." : action.label}
      </button>
    </section>
  );
}

export function WorkersWorkspace({ data }: { data: WorkersData }) {
  const router = useRouter();
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState("");

  async function cancelQueued() {
    const queuedCount = data.encodingQueuedCount;
    const confirmed = window.confirm(
      `Cancel ${queuedCount} queued job${queuedCount === 1 ? "" : "s"}?\n\n` +
        "Jobs already picked up by a worker (downloading, encoding, uploading) keep running and finish normally.\n" +
        "Only jobs still waiting in the queue are removed."
    );
    if (!confirmed) {
      return;
    }
    setCancelBusy(true);
    setCancelError("");
    try {
      const response = await fetch("/api/encoding-jobs/cancel-queued", { method: "POST" });
      const payload = (await response.json().catch(() => null)) as { ok?: boolean; cancelled?: number; error?: string } | null;
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || `cancel failed (HTTP ${response.status})`);
      }
      router.refresh();
    } catch (cause) {
      setCancelError(cause instanceof Error ? cause.message : "cancel queued jobs failed");
    } finally {
      setCancelBusy(false);
    }
  }

  return (
    <main className="workersPage">
      <header className="workersHeader">
        <div>
          <h1>Shot workers</h1>
          <p>Shot split workers, inference workers, and remote servers</p>
        </div>
        <div className="workersSummary">
          <div>
            <strong>{formatRatio(data.cluster.wallRatio)}</strong>
            <span>cluster vs playback</span>
          </div>
          <div>
            <strong>{formatRatio(data.cluster.workerRatio)}</strong>
            <span>worker elapsed speed</span>
          </div>
          <div>
            <strong>{formatRatio(data.inferenceCluster.ratio)}</strong>
            <span>inference last 5m</span>
          </div>
          <div>
            <strong>{data.inferenceCluster.doneCount}</strong>
            <span>shots in 5m</span>
          </div>
        </div>
      </header>

      <MaintenancePanel initial={data.maintenance} />

      <section className="workersSection">
        <div className="workersSectionHeader">
          <h2>Infrastructure Monitors</h2>
        </div>
        <div className="workerCardGrid">
          {data.monitorAgents.length ? data.monitorAgents.map((agent) => <MonitorAgentCard agent={agent} key={agent.key} />) : (
            <div className="workersEmpty">No infrastructure monitor has reported yet.</div>
          )}
        </div>
      </section>

      <section className="workersSection">
        <div className="workersSectionHeader">
          <h2>
            Shot Split Worker
            <span className="workersQueuedCount">{data.encodingQueuedCount} queued</span>
            <button
              type="button"
              className="workersQueueCancelButton"
              disabled={cancelBusy || data.encodingQueuedCount === 0}
              title="Remove every job still waiting in the queue. Jobs already picked up by a worker finish normally."
              onClick={() => void cancelQueued()}
            >
              {cancelBusy ? "…" : "cancel"}
            </button>
            {cancelError ? <span className="workersQueueCancelError">{cancelError}</span> : null}
          </h2>
          <span>
            {formatDuration(data.cluster.processedMs)} video / {formatDuration(data.cluster.activeWallMs)} wall
          </span>
        </div>
        <div className="workerCardGrid">
          {data.workers.length ? data.workers.map((worker) => <WorkerCard key={worker.id} worker={worker} />) : (
            <div className="workersEmpty">No worker has registered yet.</div>
          )}
        </div>
      </section>

      <section className="workersSection">
        <div className="workersSectionHeader">
          <h2>Inference Workers</h2>
        </div>
        <div className="workerCardGrid">
          {data.inferenceWorkers.length ? data.inferenceWorkers.map((worker) => <WorkerCard key={worker.id} worker={worker} />) : (
            <div className="workersEmpty">No inference worker has registered yet.</div>
          )}
        </div>
      </section>

      <section className="workersSection">
        <div className="workersSectionHeader">
          <h2>Inference Servers</h2>
        </div>
        <div className="workerCardGrid">
          {data.remoteServers.length ? (
            data.remoteServers.map((server) => <RemoteServerCard key={server.id} server={server} />)
          ) : (
            <div className="workersEmpty">No remote server configured.</div>
          )}
        </div>
      </section>

      <section className="workersSection">
        <div className="workersSectionHeader">
          <h2>Recent Encoding Runs</h2>
        </div>
        <div className="workerRunTable">
          <div className="workerRunHeader">
            <span>Worker</span>
            <span>Video</span>
            <span>Status</span>
            <span>Speed</span>
            <span>Video</span>
            <span>Elapsed</span>
            <span>Shots</span>
            <span>Finished</span>
          </div>
          {data.recentRuns.length ? (
            data.recentRuns.map((run) => <RecentRunRow key={run.id} run={run} />)
          ) : (
            <div className="workersEmpty">No completed worker run has been recorded yet.</div>
          )}
        </div>
      </section>
    </main>
  );
}
